// src/utils/quote/kayttokohdePresets.js
// Käyttökohde → pre-answered RASITUSLUOKKA_TREE nodes + raeKokoId/notkeusId
// defaults + optional käyttökohde-specific post-question.
//
// `answers` values are the `id` strings on the referenced tree node's options
// (NOT the labels — labels can be reworded in rasitusluokat.js without touching
// this file). `postQuestion.options[].override.terminalId` must match a `term_*`
// node id in the same tree. `notkeusId` / `raeKokoId` values are FKs to
// betoniNotkeus / betoniRaeKoko (see quoteConstants.js for the lookup tables).
//
// Source of truth for these mappings: docs/design/spec-tree.md.
//
// i18n: KAYTTOKOHDE_PRESETS is a function of `t` (its only consumers —
// SpecGuideModal.jsx, betoniLaatuSelvitys.js, this file's own test, and
// scripts/audit-rasitusluokka-tree.js — are all wizard-scoped, so unlike
// quoteConstants.js's OPTIONS arrays this conversion is fully contained). Only
// `question` / `label` / `description` go through `t()` — `id`s and
// `override.terminalId` are data and stay untouched.

// Four käyttökohdes (lattia, seinat, holvi, pilarit) share the same
// indoor-dry-reinforced tree path. Factored out so the differences (raeKokoId,
// optional postQuestion) read at a glance.
const INDOOR_DRY_REINFORCED = {
    sisaUlko: "sisa",
    raudoitettu: "kylla",
    kosteustila: "kuivaNormaaliSisa",
};

/** @param {(key: string) => string} t */
export function kayttokohdePresets(t) {
    const kp = "wizard.kayttokohdePresets.";
    return {
        anturat: {
            // Useimmat anturat ovat maan alla, vakaassa kosteassa maaperässä,
            // routarajan alapuolella — ei pakkasrasitusta → XC2 standardin mukaan.
            // Matala / osin säälle altis antura ohjataan postQuestionin kautta
            // XF1-luokkaan (sama mix; vain rasitusluokka kovenee).
            answers: {
                sisaUlko: "ulko",
                pakkasrasitus: "ei",
                maanAllaTaiVedessa: "kylla",
            },
            notkeusId: 3,   // S3
            raeKokoId: 3,   // 32 mm
            postQuestion: {
                id: "anturaSaalleAltis",
                question: t(kp + "anturat.question"),
                options: [
                    { id: "eiSaalleAltis", label: t(kp + "anturat.eiSaalleAltisLabel"), override: null },
                    {
                        id: "kyllaSaalleAltis",
                        label: t(kp + "anturat.kyllaSaalleAltisLabel"),
                        description: t(kp + "anturat.kyllaSaalleAltisDescription"),
                        override: { terminalId: "term_XF1" },
                    },
                ],
            },
        },
        sokkeli: {
            answers: {
                sisaUlko: "ulko",
                pakkasrasitus: "kylla",
                kloridirasitusUlko: "ei",
                pystyVaaka: "pysty",
            },
            notkeusId: 3,   // S3
            raeKokoId: 1,   // 16 mm
        },
        harkko: {
            // Same tree path as sokkeli → term_XF1 (XC3, XC4, XF1 · C30/37). Harkko-täyttöbetoni
            // diverges only in mix consistency: S4 to flow into block cells, 8 mm aggregate to fit.
            answers: {
                sisaUlko: "ulko",
                pakkasrasitus: "kylla",
                kloridirasitusUlko: "ei",
                pystyVaaka: "pysty",
            },
            notkeusId: 4,   // S4
            raeKokoId: 4,   // 8 mm
        },
        lattia: {
            answers: { ...INDOOR_DRY_REINFORCED },
            notkeusId: 3,   // S3
            raeKokoId: 1,   // 16 mm
            postQuestion: {
                id: "autotalli",
                question: t(kp + "lattia.question"),
                options: [
                    { id: "ei", label: t(kp + "lattia.eiLabel"), override: null },
                    {
                        id: "kylla",
                        label: t(kp + "lattia.kyllaLabel"),
                        description: t(kp + "lattia.kyllaDescription"),
                        override: {
                            terminalId: "term_XD1",
                            notkeusId: 3,    // S3
                            raeKokoId: 2,    // hieno 16 mm
                        },
                    },
                ],
            },
        },
        seinat:  { answers: { ...INDOOR_DRY_REINFORCED }, notkeusId: 3, raeKokoId: 1 }, // S3, 16 mm
        holvi:   { answers: { ...INDOOR_DRY_REINFORCED }, notkeusId: 3, raeKokoId: 1 }, // S3, 16 mm
        pilarit: { answers: { ...INDOOR_DRY_REINFORCED }, notkeusId: 4, raeKokoId: 4 }, // S4, 8 mm
        portaat: {
            // Modal asks Sisä/Ulko + downstream. S2 applies regardless of branch.
            answers: {},
            notkeusId: 2,   // S2
            raeKokoId: 1,   // 16 mm
        },
        muu: {
            answers: {},
            notkeusId: "",
            raeKokoId: "",
        },
    };
}
